/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Typography, Row, Col, Switch, Spin } from 'antd';
import { DashboardOutlined, LineChartOutlined, ArrowUpOutlined, ArrowDownOutlined, SafetyOutlined, CheckCircleOutlined, BarChartOutlined, AppstoreOutlined, TeamOutlined, ShoppingOutlined, WarningOutlined, LoadingOutlined } from '@ant-design/icons';
import axiosConfig from '../../services/axiosConfig';

const { Title } = Typography;

const DashboardLog = ({
  shift = 'day',
  dprLogId,
  sowId
}) => {
  const [overviewPeriod, setOverviewPeriod] = useState('Monthly');
  const [bothShift, setBothShift] = useState(false);
  const [dashboardMetrics, setDashboardMetrics] = useState(null);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);

  // Fetch dashboard metrics
  const fetchDashboardMetrics = useCallback(async () => {
    if (!dprLogId) return;

    setIsLoadingMetrics(true);
    try {
      // Determine shift value: if bothShift is true, use "both", otherwise use the shift prop
      const shiftValue = bothShift ? 'both' : shift;
      
      const response = await axiosConfig.get(`/dpr-dashboard/metrics`, {
        params: {
          dprLogId: dprLogId,
          shift: shiftValue
        }
      });

      if (response.data && response.data.data) {
        setDashboardMetrics(response.data.data);
        console.log('Dashboard metrics fetched:', response.data.data);
      }
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
    } finally {
      setIsLoadingMetrics(false);
    }
  }, [dprLogId, shift, bothShift]);

  // Fetch metrics when component mounts or when dependencies change
  useEffect(() => {
    fetchDashboardMetrics();
  }, [fetchDashboardMetrics]);

  const dashboardSections = [
    { key: 'safety', title: 'Safety', fullWidth: false },
    { key: 'quality', title: 'Quality', fullWidth: false },
    { key: 'personnel', title: 'Personnel', fullWidth: false },
    { key: 'pipeTally', title: 'Pipe tally', fullWidth: false },
    { key: 'productionImpact', title: 'Production Impact', fullWidth: true },
    { key: 'consumables', title: 'Consumables', fullWidth: false },
    { key: 'productivity', title: 'Productivity', fullWidth: false }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <style>
        {`
          .dashboard-accordion .ant-collapse-item {
            background-color: transparent !important;
            border: 1px solid rgba(255, 255, 255, 0.2) !important;
            border-radius: 8px !important;
            margin-bottom: 16px !important;
          }
          .dashboard-accordion .ant-collapse-header {
            background-color: rgba(255, 255, 255, 0.05) !important;
            border-radius: 8px !important;
            padding: 16px 20px !important;
            color: #fff !important;
          }
          .dashboard-accordion .ant-collapse-header:hover {
            background-color: rgba(255, 255, 255, 0.1) !important;
          }
          .dashboard-accordion .ant-collapse-content {
            background-color: transparent !important;
            border: none !important;
          }
          .dashboard-accordion .ant-collapse-content-box {
            padding: 20px !important;
          }
          .dashboard-card {
            border-radius: 8px !important;
            background-color: rgba(255, 255, 255, 0.05) !important;
            border: 1px solid rgba(255, 255, 255, 0.2) !important;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
          }
          .dashboard-card .ant-card-body {
            padding: 20px !important;
            background: transparent !important;
          }
          .dashboard-card-equal-height {
            height: 100% !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .dashboard-card-equal-height .ant-card-body {
            flex: 1 !important;
            display: flex !important;
            flex-direction: column !important;
          }
          .dashboard-select .ant-select-selector {
            background-color: rgba(255, 255, 255, 0.1) !important;
            border-color: rgba(255, 255, 255, 0.2) !important;
            color: #fff !important;
            border-radius: 4px !important;
          }
          .dashboard-select .ant-select-selection-item {
            color: #fff !important;
          }
          .dashboard-select .ant-select-arrow {
            color: rgba(255, 255, 255, 0.65) !important;
          }
          .dashboard-select:hover .ant-select-selector {
            border-color: rgba(255, 255, 255, 0.3) !important;
          }
          .dashboard-select.ant-select-focused .ant-select-selector {
            border-color: rgba(255, 77, 79, 0.6) !important;
            box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
          }
          /* Light theme overrides */
          .light-theme h2[style*="color: rgb(255, 255, 255)"],
          .light-theme h2[style*="color:#fff"],
          .light-theme h4[style*="color: rgb(255, 255, 255)"],
          .light-theme h4[style*="color:#fff"],
          .light-theme span[style*="color: rgb(255, 255, 255)"],
          .light-theme span[style*="color:#fff"],
          .light-theme div[style*="color: rgb(255, 255, 255)"],
          .light-theme div[style*="color:#fff"],
          .light-theme label[style*="color: rgb(255, 255, 255)"],
          .light-theme label[style*="color:#fff"] {
            color: #000 !important;
          }
          .light-theme .dashboard-card {
            background-color: rgba(0, 0, 0, 0.03) !important;
            border-color: rgba(0, 0, 0, 0.15) !important;
          }
          /* Override all rgba white colors in light theme */
          .light-theme div[style*="color: rgba(255, 255, 255"],
          .light-theme span[style*="color: rgba(255, 255, 255"] {
            color: rgba(0, 0, 0, 0.75) !important;
          }
          /* Input and textarea text and placeholder in light theme */
          .light-theme input[style*="color: rgb(255, 255, 255)"],
          .light-theme input[style*="color:#fff"],
          .light-theme textarea[style*="color: rgb(255, 255, 255)"],
          .light-theme textarea[style*="color:#fff"],
          .light-theme .ant-input[style*="color: rgb(255, 255, 255)"],
          .light-theme .ant-input[style*="color:#fff"] {
            color: rgba(0, 0, 0, 0.85) !important;
          }
          .light-theme input::placeholder,
          .light-theme textarea::placeholder,
          .light-theme .ant-input::placeholder {
            color: rgba(0, 0, 0, 0.45) !important;
          }
          .light-theme .ant-input-number-input {
            color: rgba(0, 0, 0, 0.85) !important;
          }
          /* Select component text in light theme */
          .light-theme .ant-select-selection-item {
            color: rgba(0, 0, 0, 0.85) !important;
          }
          .light-theme .ant-select-selection-placeholder {
            color: rgba(0, 0, 0, 0.45) !important;
          }
          .light-theme .dashboard-select .ant-select-selection-item {
            color: rgba(0, 0, 0, 0.85) !important;
          }
          .light-theme .dashboard-select .ant-select-selector {
            background-color: rgba(0, 0, 0, 0.05) !important;
            border-color: rgba(0, 0, 0, 0.15) !important;
          }
          .light-theme .dashboard-select:hover .ant-select-selector {
            border-color: rgba(0, 0, 0, 0.25) !important;
          }
          .light-theme .dashboard-select.ant-select-focused .ant-select-selector {
            background-color: rgba(0, 0, 0, 0.08) !important;
            border-color: rgba(255, 77, 79, 0.6) !important;
          }
        `}
      </style>

      {/* Header Section */}
      <div style={{ marginBottom: '24px', marginTop: '-23px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: '#ff4d4f',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '12px'
            }}>
              <DashboardOutlined style={{ color: '#fff', fontSize: '18px' }} />
            </div>
            <Title level={2} style={{ margin: 0, color: '#fff', fontSize: '24px', fontWeight: 600 }}>
              Dashboard
            </Title>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#fff', fontSize: '14px', fontWeight: 400 }}>Both shift</span>
              <Switch
                checked={bothShift}
                onChange={setBothShift}
                style={{
                  backgroundColor: bothShift ? '#ff4d4f' : 'rgba(255, 255, 255, 0.2)'
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {isLoadingMetrics ? (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          flexDirection: 'column',
          gap: '16px'
        }}>
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 48, color: '#ff4d4f' }} spin />}
            size="large"
          />
          <div style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '16px', fontWeight: 400 }}>
            Loading Dashboard...
          </div>
        </div>
      ) : (
        <>
          {/* Dashboard Grid */}
          <Row gutter={[16, 16]}>
            {dashboardSections.map((item) => (
              <Col
                xs={24}
                lg={item.fullWidth ? 24 : 12}
                key={item.key}
                style={(item.title === 'Safety' || item.title === 'Quality' || item.title === 'Personnel' || item.title === 'Pipe tally' || item.title === 'Consumables' || item.title === 'Productivity') ? { display: 'flex' } : {}}
          >
                <Card 
                  className={`dashboard-card ${(item.title === 'Safety' || item.title === 'Quality' || item.title === 'Personnel' || item.title === 'Pipe tally' || item.title === 'Consumables' || item.title === 'Productivity') ? 'dashboard-card-equal-height' : ''}`}
                  style={(item.title === 'Safety' || item.title === 'Quality' || item.title === 'Personnel' || item.title === 'Pipe tally' || item.title === 'Consumables' || item.title === 'Productivity') ? { width: '100%' } : {}}
            >
              {item.title === 'Safety' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  {/* Safety Header */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(82, 196, 26, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid rgba(82, 196, 26, 0.5)'
                    }}>
                      <SafetyOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>
                      1.0 SAFETY
                    </Title>
                  </div>

                  {/* Safety Metrics Table */}
                  <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    overflow: 'hidden'
                  }}>
                    {/* Table Header */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      padding: '12px 16px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '14px',
                        fontWeight: 600
                      }}>
                        Metric
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '14px',
                        fontWeight: 600,
                        textAlign: 'center'
                      }}>
                        Today
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '14px',
                        fontWeight: 600,
                        textAlign: 'center'
                      }}>
                        To Date
                      </div>
                    </div>
                    {/* Table Rows */}
                    {dashboardMetrics?.safetyMetrics?.entriesList && dashboardMetrics.safetyMetrics.entriesList.length > 0 ? (
                      dashboardMetrics.safetyMetrics.entriesList.map((entry, index) => (
                        <div
                          key={entry.id || entry.keyName || index}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '2fr 1fr 1fr',
                            padding: '12px 16px',
                            borderBottom: index < dashboardMetrics.safetyMetrics.entriesList.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                            backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.02)' : 'transparent'
                          }}
                        >
                          <div style={{ 
                            color: 'rgba(255, 255, 255, 0.85)', 
                            fontSize: '14px',
                            fontWeight: 400
                          }}>
                            {entry.name || entry.keyName}
                          </div>
                          <div style={{ 
                            color: '#fff', 
                            fontSize: '14px',
                            fontWeight: 400,
                            textAlign: 'center'
                          }}>
                            {entry.todayValue ?? 0}
                          </div>
                          <div style={{ 
                            color: '#fff', 
                            fontSize: '14px',
                            fontWeight: 400,
                            textAlign: 'center'
                          }}>
                            {entry.toDateValue ?? 0}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{
                        padding: '20px',
                        textAlign: 'center',
                        color: 'rgba(255, 255, 255, 0.65)',
                        fontSize: '14px'
                      }}>
                        No safety data available
                      </div>
                    )}
                  </div>

                  {/* Summary Totals - Separate from table */}
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '12px',
                    marginTop: '20px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.65)', 
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        No. of Incidents Today
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '16px',
                        fontWeight: 600,
                        textAlign: 'right'
                      }}>
                        {dashboardMetrics?.safetyMetrics?.today ?? 0}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.65)', 
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        No. of Incidents To Date
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '16px',
                        fontWeight: 600,
                        textAlign: 'right'
                      }}>
                        {dashboardMetrics?.safetyMetrics?.toDate ?? 0}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0'
                    }}>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.65)', 
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        Total No. of Incidents
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '16px',
                        fontWeight: 600,
                        textAlign: 'right'
                      }}>
                        {dashboardMetrics?.safetyMetrics?.total ?? 0}
                      </div>
                    </div>
                  </div>
                </div>
              ) : item.title === 'Quality' ? (
                      <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  {/* Quality Header */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(24, 144, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '2px solid rgba(24, 144, 255, 0.5)'
                    }}>
                      <CheckCircleOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>
                      2.0 QUALITY
                    </Title>
                  </div>

                  {/* Quality Metrics Table */}
                  <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    overflow: 'hidden'
                  }}>
                    {/* Table Header */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      padding: '12px 16px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '14px',
                        fontWeight: 600
                      }}>
                        Metric
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '14px',
                        fontWeight: 600,
                        textAlign: 'center'
                      }}>
                        Today
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '14px',
                        fontWeight: 600,
                        textAlign: 'center'
                      }}>
                        To Date
                      </div>
                    </div>
                    {/* Table Rows */}
                    {dashboardMetrics?.qualityReport?.entriesList && dashboardMetrics.qualityReport.entriesList.length > 0 ? (
                      dashboardMetrics.qualityReport.entriesList.map((entry, index) => (
                        <div
                          key={entry.key || index}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '2fr 1fr 1fr',
                            padding: '12px 16px',
                            borderBottom: index < dashboardMetrics.qualityReport.entriesList.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                            backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.02)' : 'transparent'
                          }}
                        >
                          <div style={{ 
                            color: 'rgba(255, 255, 255, 0.85)', 
                            fontSize: '14px',
                            fontWeight: 400
                          }}>
                            {entry.name || entry.key}
                          </div>
                          <div style={{ 
                            color: '#fff', 
                            fontSize: '14px',
                            fontWeight: 400,
                            textAlign: 'center'
                          }}>
                            {entry.today ?? 0}
                          </div>
                          <div style={{ 
                            color: '#fff', 
                            fontSize: '14px',
                            fontWeight: 400,
                            textAlign: 'center'
                          }}>
                            {entry.toDate ?? 0}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div style={{
                        padding: '20px',
                        textAlign: 'center',
                        color: 'rgba(255, 255, 255, 0.65)',
                        fontSize: '14px'
                      }}>
                        No quality data available
                      </div>
                    )}
                  </div>

                  {/* Summary Totals - Separate from table */}
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '12px',
                    marginTop: '20px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.65)', 
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        Total Issues Today
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '16px',
                        fontWeight: 600,
                        textAlign: 'right'
                      }}>
                        {dashboardMetrics?.qualityReport?.totalIssues ?? 0}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0'
                    }}>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.65)', 
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        Total Issues To Date
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '16px',
                        fontWeight: 600,
                        textAlign: 'right'
                      }}>
                        {dashboardMetrics?.qualityReport?.toDate ?? 0}
                      </div>
                    </div>
                  </div>
                </div>
              ) : item.title === 'Productivity' ? (
                        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  {/* Productivity Header */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(139, 69, 19, 0.3)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <BarChartOutlined style={{ color: '#8B4513', fontSize: '20px' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>
                      3.0 PRODUCTIVITY
                    </Title>
                  </div>

                  {/* Productivity Metrics Table */}
                  <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    overflow: 'hidden'
                  }}>
                    {/* Table Header */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      padding: '12px 16px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '14px',
                        fontWeight: 600,
                        textAlign: 'left'
                      }}>
                        Metric
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '14px',
                        fontWeight: 600,
                        textAlign: 'right'
                      }}>
                        Value
                      </div>
                    </div>

                    {/* Table Rows */}
                    <div>
                      {/* Welded at Station 1 */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr',
                        padding: '12px 16px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        backgroundColor: 'rgba(255, 255, 255, 0.02)'
                      }}>
                        <div style={{ 
                          color: 'rgba(255, 255, 255, 0.85)', 
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'left'
                        }}>
                          Welded at Station 1
                        </div>
                        <div style={{ 
                          color: '#fff', 
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'right'
                        }}>
                          {dashboardMetrics?.productivity?.totalWelds ?? 0}
                        </div>
                      </div>

                      {/* RT Completed */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr',
                        padding: '12px 16px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        backgroundColor: 'transparent'
                      }}>
                        <div style={{ 
                          color: 'rgba(255, 255, 255, 0.85)', 
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'left'
                        }}>
                          RT Completed
                        </div>
                        <div style={{ 
                          color: '#fff', 
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'right'
                        }}>
                          {dashboardMetrics?.productivity?.rtCompleted ?? 0}
                        </div>
                      </div>

                      {/* RT Accepted */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr',
                        padding: '12px 16px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        backgroundColor: 'rgba(255, 255, 255, 0.02)'
                      }}>
                        <div style={{ 
                          color: 'rgba(255, 255, 255, 0.85)', 
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'left'
                        }}>
                          RT Accepted
                        </div>
                        <div style={{ 
                          color: '#52c41a', 
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'right'
                        }}>
                          {dashboardMetrics?.productivity?.rtAccepted ?? 0}
                        </div>
                      </div>

                      {/* RT Rejected */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr',
                        padding: '12px 16px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        backgroundColor: 'transparent'
                      }}>
                        <div style={{ 
                          color: 'rgba(255, 255, 255, 0.85)', 
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'left'
                        }}>
                          RT Rejected
                        </div>
                        <div style={{ 
                          color: '#ff4d4f', 
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'right'
                        }}>
                          {dashboardMetrics?.productivity?.rtRejected ?? 0}
                        </div>
                      </div>

                      {/* Acceptance Rate */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr',
                        padding: '12px 16px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        backgroundColor: 'rgba(255, 255, 255, 0.02)'
                      }}>
                        <div style={{ 
                          color: 'rgba(255, 255, 255, 0.85)', 
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'left'
                        }}>
                          Acceptance Rate
                        </div>
                        <div style={{ 
                          color: '#ff4d4f', 
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'right'
                        }}>
                          {dashboardMetrics?.productivity?.acceptanceRate ? `${dashboardMetrics.productivity.acceptanceRate.toFixed(2)}%` : '0.00%'}
                        </div>
                      </div>

                      {/* Repairs */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr',
                        padding: '12px 16px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        backgroundColor: 'transparent'
                      }}>
                        <div style={{ 
                          color: 'rgba(255, 255, 255, 0.85)', 
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'left'
                        }}>
                          Repairs
                        </div>
                        <div style={{ 
                          color: '#fff', 
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'right'
                        }}>
                          {dashboardMetrics?.productivity?.repairs ?? 0}
                        </div>
                      </div>

                      {/* Cutoffs */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr',
                        padding: '12px 16px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        backgroundColor: 'rgba(255, 255, 255, 0.02)'
                      }}>
                        <div style={{ 
                          color: 'rgba(255, 255, 255, 0.85)', 
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'left'
                        }}>
                          Cutoffs
                        </div>
                        <div style={{ 
                          color: '#fff', 
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'right'
                        }}>
                          {dashboardMetrics?.productivity?.cutoffs ?? 0}
                        </div>
                      </div>

                      {/* Blow Through */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr',
                        padding: '12px 16px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        backgroundColor: 'transparent'
                      }}>
                        <div style={{ 
                          color: 'rgba(255, 255, 255, 0.85)', 
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'left'
                        }}>
                          Blow Through
                        </div>
                        <div style={{ 
                          color: '#fff', 
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'right'
                        }}>
                          {dashboardMetrics?.productivity?.blowThrough ?? 0}
                        </div>
                      </div>

                      {/* First Joint */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr',
                        padding: '12px 16px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                        backgroundColor: 'rgba(255, 255, 255, 0.02)'
                      }}>
                        <div style={{ 
                          color: 'rgba(255, 255, 255, 0.85)', 
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'left'
                        }}>
                          First Joint
                        </div>
                        <div style={{ 
                          color: '#fff', 
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'right'
                        }}>
                          {dashboardMetrics?.productivity?.firstJoint || '-'}
                        </div>
                      </div>

                      {/* Last Joint */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr',
                        padding: '12px 16px',
                        backgroundColor: 'transparent'
                      }}>
                        <div style={{ 
                          color: 'rgba(255, 255, 255, 0.85)', 
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'left'
                        }}>
                          Last Joint
                        </div>
                        <div style={{ 
                          color: '#fff', 
                          fontSize: '14px',
                          fontWeight: 400,
                          textAlign: 'right'
                        }}>
                          {dashboardMetrics?.productivity?.lastJoint || '-'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : item.title === 'Pipe tally' ? (
                          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  {/* Pipe Tally Header */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(255, 20, 147, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <AppstoreOutlined style={{ color: '#ff1493', fontSize: '20px' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>
                      Pipe Tally
                    </Title>
                  </div>

                  {/* Pipe Tally Table */}
                  <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    overflow: 'hidden',
                    marginBottom: '20px'
                  }}>
                    {/* Table Header */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      padding: '12px 16px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '14px',
                        fontWeight: 600
                      }}>
                        Test Type
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '14px',
                        fontWeight: 600,
                        textAlign: 'center'
                      }}>
                        Today
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '14px',
                        fontWeight: 600,
                        textAlign: 'center'
                      }}>
                        To Date
                      </div>
                    </div>
                    {/* Table Rows */}
                    {/* Radiographic Testing (RT) */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr',
                      padding: '12px 16px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                      backgroundColor: 'rgba(255, 255, 255, 0.02)'
                    }}>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.85)', 
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        Radiographic Testing (RT)
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '14px',
                        fontWeight: 400,
                        textAlign: 'center'
                      }}>
                        {dashboardMetrics?.pipeTally?.rt?.value ?? 0}
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '14px',
                        fontWeight: 400,
                        textAlign: 'center'
                      }}>
                        {dashboardMetrics?.pipeTally?.rt?.toDate ?? 0}
                      </div>
                    </div>
                    {/* Fit-up Joint Check (FJC) */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1fr',
                      padding: '12px 16px',
                      backgroundColor: 'transparent'
                    }}>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.85)', 
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        Fit-up Joint Check (FJC)
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '14px',
                        fontWeight: 400,
                        textAlign: 'center'
                      }}>
                        {dashboardMetrics?.pipeTally?.fjc?.value ?? 0}
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '14px',
                        fontWeight: 400,
                        textAlign: 'center'
                      }}>
                        {dashboardMetrics?.pipeTally?.fjc?.toDate ?? 0}
                      </div>
                    </div>
                  </div>

                  {/* Summary Information */}
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '12px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.65)', 
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        Total Completed:
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '16px',
                        fontWeight: 600,
                        textAlign: 'right'
                      }}>
                        {dashboardMetrics?.pipeTally?.totalCompleted ?? 0}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.65)', 
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        First Joint:
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '16px',
                        fontWeight: 600,
                        textAlign: 'right'
                      }}>
                        {dashboardMetrics?.pipeTally?.firstJointWeld || '-'}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0'
                    }}>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.65)', 
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        Last Joint:
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '16px',
                        fontWeight: 600,
                        textAlign: 'right'
                      }}>
                        {dashboardMetrics?.pipeTally?.lastJointWeld || '-'}
                      </div>
                    </div>
                  </div>
                </div>
              ) : item.title === 'Personnel' ? (
                            <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  {/* Personnel Header */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(114, 46, 209, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <TeamOutlined style={{ color: '#722ed1', fontSize: '20px' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>
                      4.0 PERSONNEL
                    </Title>
                  </div>

                  {/* Personnel Metrics Table - Only show if entriesList is not empty */}
                  {dashboardMetrics?.personnel?.entriesList && dashboardMetrics.personnel.entriesList.length > 0 ? (
                    <div style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      overflow: 'hidden',
                      marginBottom: '20px'
                    }}>
                      {/* Table Header */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1fr',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        padding: '12px 16px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <div style={{ 
                          color: '#fff', 
                          fontSize: '14px',
                          fontWeight: 600
                        }}>
                          Metric
                        </div>
                        <div style={{ 
                          color: '#fff', 
                          fontSize: '14px',
                          fontWeight: 600,
                          textAlign: 'center'
                        }}>
                          Planned
                        </div>
                        <div style={{ 
                          color: '#fff', 
                          fontSize: '14px',
                          fontWeight: 600,
                          textAlign: 'center'
                        }}>
                          Actual
                        </div>
                      </div>
                      {/* Table Rows */}
                      {dashboardMetrics.personnel.entriesList.map((entry, index) => (
                        <div
                          key={entry.id || entry.keyName || index}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '2fr 1fr 1fr',
                            padding: '12px 16px',
                            borderBottom: index < dashboardMetrics.personnel.entriesList.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                            backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.02)' : 'transparent'
                          }}
                        >
                          <div style={{ 
                            color: 'rgba(255, 255, 255, 0.85)', 
                            fontSize: '14px',
                            fontWeight: 400
                          }}>
                            {entry.name || entry.keyName}
                          </div>
                          <div style={{ 
                            color: '#fff', 
                            fontSize: '14px',
                            fontWeight: 400,
                            textAlign: 'center'
                          }}>
                            {bothShift 
                              ? ((entry.plannedDayShift ?? 0) + (entry.plannedNightShift ?? 0))
                              : (shift === 'day' ? (entry.plannedDayShift ?? 0) : (entry.plannedNightShift ?? 0))
                            }
                          </div>
                          <div style={{ 
                            color: '#fff', 
                            fontSize: '14px',
                            fontWeight: 400,
                            textAlign: 'center'
                          }}>
                            {bothShift 
                              ? ((entry.actualDayShift ?? 0) + (entry.actualNightShift ?? 0))
                              : (shift === 'day' ? (entry.actualDayShift ?? 0) : (entry.actualNightShift ?? 0))
                            }
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {/* Summary Totals - Always show */}
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '12px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.65)', 
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        Planned
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '16px',
                        fontWeight: 600,
                        textAlign: 'right'
                      }}>
                        {dashboardMetrics?.personnel?.planned ?? 0}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.65)', 
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        Actual
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '16px',
                        fontWeight: 600,
                        textAlign: 'right'
                      }}>
                        {dashboardMetrics?.personnel?.actual ?? 0}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0'
                    }}>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.65)', 
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        Total
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '16px',
                        fontWeight: 600,
                        textAlign: 'right'
                      }}>
                        {dashboardMetrics?.personnel?.total ?? 0}
                      </div>
                    </div>
                  </div>
                </div>
              ) : item.title === 'Consumables' ? (
                              <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  {/* Consumables Header */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(82, 196, 26, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <ShoppingOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>
                      4.0 CONSUMABLES
                    </Title>
                  </div>

                  {/* Consumables Metrics Table - Only show if entriesList is not empty */}
                  {dashboardMetrics?.consumables?.entriesList && dashboardMetrics.consumables.entriesList.length > 0 ? (
                    <div style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      overflow: 'hidden',
                      marginBottom: '20px'
                    }}>
                      {/* Table Header */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 1fr',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        padding: '12px 16px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <div style={{ 
                          color: '#fff', 
                          fontSize: '14px',
                          fontWeight: 600
                        }}>
                          Consumable Name
                        </div>
                        <div style={{ 
                          color: '#fff', 
                          fontSize: '14px',
                          fontWeight: 600,
                          textAlign: 'center'
                        }}>
                          Product Code
                        </div>
                        <div style={{ 
                          color: '#fff', 
                          fontSize: '14px',
                          fontWeight: 600,
                          textAlign: 'center'
                        }}>
                          Used Today
                        </div>
                      </div>
                      {/* Table Rows */}
                      {dashboardMetrics.consumables.entriesList.map((entry, index) => (
                        <div
                          key={entry.id || index}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '2fr 1fr 1fr',
                            padding: '12px 16px',
                            borderBottom: index < dashboardMetrics.consumables.entriesList.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                            backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.02)' : 'transparent'
                          }}
                        >
                          <div style={{ 
                            color: 'rgba(255, 255, 255, 0.85)', 
                            fontSize: '14px',
                            fontWeight: 400
                          }}>
                            {entry.consumableName || '-'}
                          </div>
                          <div style={{ 
                            color: '#fff', 
                            fontSize: '14px',
                            fontWeight: 400,
                            textAlign: 'center'
                          }}>
                            {entry.productCode || '-'}
                          </div>
                          <div style={{ 
                            color: '#fff', 
                            fontSize: '14px',
                            fontWeight: 400,
                            textAlign: 'center'
                          }}>
                            {bothShift 
                              ? ((entry.usedTodayDayShift ?? 0) + (entry.usedTodayNightShift ?? 0))
                              : (shift === 'day' ? (entry.usedTodayDayShift ?? 0) : (entry.usedTodayNightShift ?? 0))
                            }
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {/* Summary Totals - Always show */}
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '12px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.65)', 
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        Total Items
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '16px',
                        fontWeight: 600,
                        textAlign: 'right'
                      }}>
                        {dashboardMetrics?.consumables?.totalItems ?? 0}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.65)', 
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        Used Today
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '16px',
                        fontWeight: 600,
                        textAlign: 'right'
                      }}>
                        {dashboardMetrics?.consumables?.usedToday ?? 0}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.65)', 
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        To Date
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '16px',
                        fontWeight: 600,
                        textAlign: 'right'
                      }}>
                        {dashboardMetrics?.consumables?.toDate ?? 0}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0'
                    }}>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.65)', 
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        Low Stock
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '16px',
                        fontWeight: 600,
                        textAlign: 'right'
                      }}>
                        {dashboardMetrics?.consumables?.lowStock ?? 0}
                      </div>
                    </div>
                  </div>
                </div>
              ) : item.title === 'Production Impact' ? (
                <div>
                  {/* Production Impact Header */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '8px',
                      backgroundColor: 'rgba(250, 173, 20, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <WarningOutlined style={{ color: '#faad14', fontSize: '20px' }} />
                    </div>
                    <Title level={4} style={{ margin: 0, color: '#fff', fontSize: '18px', fontWeight: 600 }}>
                      Production Impact
                    </Title>
                  </div>

                  {/* Production Impact Metrics Table - Only show if entriesList is not empty */}
                  {dashboardMetrics?.productionImpact?.entriesList && dashboardMetrics.productionImpact.entriesList.length > 0 ? (
                    <div style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      overflow: 'hidden',
                      marginBottom: '20px'
                    }}>
                      {/* Table Header */}
                      <div style={{
                        display: 'grid',
                                        gridTemplateColumns: '1.5fr 1.2fr 0.8fr 0.8fr 2.5fr 0.8fr',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        padding: '12px 16px',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <div style={{ 
                          color: '#fff', 
                          fontSize: '14px',
                          fontWeight: 600
                        }}>
                          Category
                        </div>
                        <div style={{ 
                          color: '#fff', 
                          fontSize: '14px',
                          fontWeight: 600,
                          textAlign: 'center'
                        }}>
                          Reason
                        </div>
                        <div style={{ 
                          color: '#fff', 
                          fontSize: '14px',
                          fontWeight: 600,
                          textAlign: 'center'
                        }}>
                          Start Time
                        </div>
                        <div style={{ 
                          color: '#fff', 
                          fontSize: '14px',
                          fontWeight: 600,
                          textAlign: 'center'
                        }}>
                          End Time
                        </div>
                        <div style={{ 
                          color: '#fff', 
                          fontSize: '14px',
                          fontWeight: 600,
                                          textAlign: 'left'
                        }}>
                                          Allocations
                        </div>
                        <div style={{ 
                          color: '#fff', 
                          fontSize: '14px',
                          fontWeight: 600,
                          textAlign: 'center'
                        }}>
                                          Total Hours
                        </div>
                      </div>
                      {/* Table Rows */}
                      {dashboardMetrics.productionImpact.entriesList.map((entry, index) => (
                        <div
                          key={entry.id || index}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1.5fr 1.2fr 0.8fr 0.8fr 2.5fr 0.8fr',
                            padding: '12px 16px',
                            borderBottom: index < dashboardMetrics.productionImpact.entriesList.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                            backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.02)' : 'transparent',
                            alignItems: 'center'
                          }}
                        >
                          <div style={{ 
                            color: 'rgba(255, 255, 255, 0.85)', 
                            fontSize: '14px',
                            fontWeight: 400
                          }}>
                            {entry.categoryTitle || '-'}
                          </div>
                          <div style={{ 
                            color: '#fff', 
                            fontSize: '14px',
                            fontWeight: 400,
                            textAlign: 'center'
                          }}>
                            {entry.reason || '-'}
                          </div>
                          <div style={{ 
                            color: '#fff', 
                            fontSize: '14px',
                            fontWeight: 400,
                            textAlign: 'center'
                          }}>
                            {entry.startTime || '-'}
                          </div>
                          <div style={{ 
                            color: '#fff', 
                            fontSize: '14px',
                            fontWeight: 400,
                            textAlign: 'center'
                          }}>
                            {entry.endTime || '-'}
                          </div>
                          <div style={{ 
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '6px',
                            alignItems: 'center'
                          }}>
                            {entry.allocations && entry.allocations.length > 0 ? (
                              entry.allocations.map((allocation, allocIndex) => (
                                <div
                                  key={allocation.id || allocIndex}
                                  style={{
                                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '4px',
                                    padding: '4px 8px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  <span style={{
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    fontSize: '12px',
                                    fontWeight: 500
                                  }}>
                                    {allocation.category}
                                  </span>
                                  <span style={{
                                    color: 'rgba(255, 255, 255, 0.5)',
                                    fontSize: '11px'
                                  }}>
                                    •
                                  </span>
                                  <span style={{
                                    color: 'rgba(255, 255, 255, 0.75)',
                                    fontSize: '11px'
                                  }}>
                                    {allocation.subCategory}
                                  </span>
                                  <span style={{
                                    color: 'rgba(255, 255, 255, 0.5)',
                                    fontSize: '11px'
                                  }}>
                                    •
                                  </span>
                                  <span style={{ 
                                    color: 'rgba(255, 255, 255, 0.85)',
                                    fontSize: '12px',
                                    fontWeight: 600
                                  }}>
                                    {allocation.hours}h
                                  </span>
                                </div>
                              ))
                            ) : (
                                <span style={{
                                  color: 'rgba(255, 255, 255, 0.45)',
                                  fontSize: '13px'
                                }}>
                                  -
                                </span>
                            )}
                          </div>
                          <div style={{
                            color: '#fff',
                            fontSize: '14px',
                            fontWeight: 400,
                            textAlign: 'center'
                          }}>
                            {entry.totalHours ?? 0}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  {/* Summary Totals - Always show */}
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '12px',
                    marginBottom: '20px'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.65)', 
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        Incidents
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '16px',
                        fontWeight: 600,
                        textAlign: 'right'
                      }}>
                        {dashboardMetrics?.productionImpact?.incidents ?? 0}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.65)', 
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        Total Hours
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '16px',
                        fontWeight: 600,
                        textAlign: 'right'
                      }}>
                        {dashboardMetrics?.productionImpact?.totalHours ?? 0}
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '12px 0'
                    }}>
                      <div style={{ 
                        color: 'rgba(255, 255, 255, 0.65)', 
                        fontSize: '14px',
                        fontWeight: 400
                      }}>
                        Allocated
                      </div>
                      <div style={{ 
                        color: '#fff', 
                        fontSize: '16px',
                        fontWeight: 600,
                        textAlign: 'right'
                      }}>
                        {dashboardMetrics?.productionImpact?.allocated ?? 0}
                      </div>
                    </div>
                  </div>

                </div>
              ) : (
                <div style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: '14px' }}>
                  {item.title} content will be displayed here.
                </div>
              )}
            </Card>
              </Col>
        ))}
          </Row>
        </>
      )}
    </div>
  );
};

export default DashboardLog;

